/**
 * assistant-confirmed-actions — the bounded "propose → review → confirm → act"
 * controls that live inside the submission-scoped AssistantPanel. This is the
 * narrow first evolution of D-068 (assistant advise-only) that the user
 * explicitly authorized: NO action happens from chat or model output — the only
 * mechanism that mutates anything is the user clicking "Confirm & apply" below,
 * and that applies through the SAME submission PATCH route as the normal Edit
 * screen (same permissions, lock policy, validation hooks, audit).
 *
 * Two controls, both submission-scoped:
 *  - Check submission blockers — READ-ONLY. Reads the canonical pre-publish
 *    checklist and shows pass/fail/unknown/manual honestly (a fail or unknown is
 *    never dressed up as a pass; a manual item is never auto-acknowledged).
 *  - Prepare draft corrections — a draft limited to three ordinary metadata
 *    fields, pre-filled from the submission's CURRENT values. The user edits,
 *    reviews before→after, and must explicitly confirm. Nothing is invented: a
 *    blank field stays the user's to fill.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  v2,
  ApiError,
  type Submission,
  type PrePublishChecklistItem,
  type PrePublishChecklistState,
} from '../../api/v2Client';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { isSequenceLocked } from '../../constants/status';

/**
 * The ONLY fields the assistant draft may touch. Kept identical in spirit to
 * the server allowlist (src/routes/submissions.ts) — the server is the gate;
 * this list only decides what the form shows.
 */
const CORRECTION_FIELDS = [
  { key: 'Applicant', label: 'Applicant' },
  { key: 'Indication', label: 'Indication' },
  { key: 'SequenceDescription', label: 'Sequence description' },
] as const;

type CorrectionKey = (typeof CORRECTION_FIELDS)[number]['key'];

function currentValue(submission: Submission, key: CorrectionKey): string {
  const raw = (submission as unknown as Record<string, unknown>)[key];
  return raw === null || raw === undefined ? '' : String(raw);
}

function buildInitialDraft(submission: Submission): Record<CorrectionKey, string> {
  const draft = {} as Record<CorrectionKey, string>;
  for (const f of CORRECTION_FIELDS) draft[f.key] = currentValue(submission, f.key);
  return draft;
}

/** Fields whose drafted value differs from the submission's current value. */
function changedFields(
  submission: Submission,
  draft: Record<CorrectionKey, string>,
): Array<(typeof CORRECTION_FIELDS)[number]> {
  return CORRECTION_FIELDS.filter((f) => draft[f.key] !== currentValue(submission, f.key));
}

const STATE_META: Record<PrePublishChecklistState, { label: string; color: string; bg: string }> = {
  pass: { label: 'Pass', color: '#166534', bg: '#dcfce7' },
  fail: { label: 'Not met', color: '#991b1b', bg: '#fee2e2' },
  unknown: { label: 'Unknown', color: '#92400e', bg: '#fef3c7' },
  manual: { label: 'Needs manual confirmation', color: '#1e3a8a', bg: '#dbeafe' },
};

const sectionStyle: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  background: 'var(--surface)',
};

const primaryButton: CSSProperties = {
  fontSize: 12,
  padding: '6px 12px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--accent, #2563eb)',
  color: '#fff',
  cursor: 'pointer',
};

const secondaryButton: CSSProperties = {
  fontSize: 12,
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  cursor: 'pointer',
};

const noticeStyle: CSSProperties = {
  fontSize: 12,
  lineHeight: 1.5,
  padding: '6px 10px',
  borderRadius: 8,
};

type BlockersState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; items: PrePublishChecklistItem[] };

type PrepState =
  | { stage: 'idle' }
  | { stage: 'loading' }
  | { stage: 'error'; message: string }
  | { stage: 'editing'; snapshot: Submission }
  | { stage: 'reviewing'; snapshot: Submission };

export function AssistantActions({ submissionId }: { submissionId: string }): ReactElement {
  const navigate = useNavigate();
  const perms = useUserPermissions();
  const canWrite = !perms.loading && !perms.failed && perms.hasPermission('Submissions', 'write');

  // Bumped whenever the submission changes. Every async handler captures the
  // token it started under and bails on resolution if it has moved on, so a
  // slow load for submission A can never land on submission B — and a drafted
  // correction can never leak across submissions.
  const tokenRef = useRef(0);
  const applyingRef = useRef(false);

  const [blockers, setBlockers] = useState<BlockersState>({ status: 'idle' });
  const [prep, setPrep] = useState<PrepState>({ stage: 'idle' });
  const [draft, setDraft] = useState<Record<CorrectionKey, string>>(() =>
    Object.fromEntries(CORRECTION_FIELDS.map((f) => [f.key, ''])) as Record<CorrectionKey, string>,
  );
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // A new submission resets everything; the token bump also invalidates any
  // in-flight load from the previous submission.
  useEffect(() => {
    tokenRef.current += 1;
    setBlockers({ status: 'idle' });
    setPrep({ stage: 'idle' });
    setReason('');
    setSaving(false);
    setSaveError(null);
    setSavedMessage(null);
    return () => { tokenRef.current += 1; };
  }, [submissionId]);

  const checkBlockers = useCallback(async () => {
    const token = tokenRef.current;
    setBlockers({ status: 'loading' });
    try {
      const res = await v2.submissions.getPrePublishChecklist(submissionId);
      if (tokenRef.current !== token) return;
      setBlockers({ status: 'loaded', items: res.items });
    } catch (err) {
      if (tokenRef.current !== token) return;
      setBlockers({
        status: 'error',
        message:
          err instanceof ApiError
            ? err.message
            : 'The submission checklist could not be loaded. Try again.',
      });
    }
  }, [submissionId]);

  const prepareDraft = useCallback(async () => {
    if (!canWrite || applyingRef.current) return;
    const token = tokenRef.current;
    setSavedMessage(null);
    setSaveError(null);
    setPrep({ stage: 'loading' });
    try {
      const submission = await v2.submissions.get(submissionId);
      if (tokenRef.current !== token) return;
      if (!submission.AssistantRevision) {
        setPrep({ stage: 'error', message: 'The current submission revision is unavailable. Refresh and try again.' });
        return;
      }
      setDraft(buildInitialDraft(submission));
      setReason('');
      setPrep({ stage: 'editing', snapshot: submission });
    } catch (err) {
      if (tokenRef.current !== token) return;
      setPrep({
        stage: 'error',
        message:
          err instanceof ApiError
            ? err.message
            : 'This submission could not be loaded. Try again.',
      });
    }
  }, [submissionId, canWrite]);

  const cancelDraft = useCallback(() => {
    if (applyingRef.current) return;
    setPrep({ stage: 'idle' });
    setReason('');
    setSaveError(null);
  }, []);

  const confirmApply = useCallback(async () => {
    if (prep.stage !== 'reviewing' || applyingRef.current || !canWrite || isSequenceLocked(prep.snapshot)) return;
    const { snapshot } = prep;
    const changes = changedFields(snapshot, draft);
    if (changes.length === 0 || reason.trim().length === 0 || !snapshot.AssistantRevision) return;
    const token = tokenRef.current;
    applyingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const fields: Record<string, string> = {};
      const expectedFields: Record<string, string> = {};
      for (const f of changes) {
        fields[f.key] = draft[f.key];
        expectedFields[f.key] = currentValue(snapshot, f.key);
      }
      await v2.submissions.applyAssistantCorrection(submissionId, {
        fields,
        reason: reason.trim(),
        expected: { UpdatedAt: snapshot.UpdatedAt, AssistantRevision: snapshot.AssistantRevision, fields: expectedFields },
      });
      if (tokenRef.current !== token) return;
      // apiFetch already dispatches SUBMISSION_DATA_CHANGED on this PATCH, so
      // the submission view refreshes itself — we only clear the draft.
      setPrep({ stage: 'idle' });
      setReason('');
      setSavedMessage('Correction applied. The submission has been updated.');
      setBlockers({ status: 'idle' });
    } catch (err) {
      if (tokenRef.current !== token) return;
      if (err instanceof ApiError && err.status === 409) {
        setSaveError(
          'This submission changed since you prepared the draft, so nothing was applied. Cancel and prepare a new draft from the current values.',
        );
      } else {
        setSaveError(
          err instanceof ApiError
            ? err.message
            : 'The save could not be confirmed. Refresh and check the current values before trying again.',
        );
      }
    } finally {
      applyingRef.current = false;
      if (tokenRef.current === token) setSaving(false);
    }
  }, [prep, draft, reason, canWrite, submissionId]);

  const snapshot = prep.stage === 'editing' || prep.stage === 'reviewing' ? prep.snapshot : null;
  const locked = snapshot ? isSequenceLocked(snapshot) : false;
  const changes = snapshot ? changedFields(snapshot, draft) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="assistant-actions">
      {/* ---- Check submission blockers (read-only) ---- */}
      <section style={sectionStyle} data-testid="assistant-blockers">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <strong style={{ fontSize: 13 }}>Check submission blockers</strong>
          <button
            type="button"
            data-testid="assistant-blockers-run"
            onClick={() => void checkBlockers()}
            disabled={blockers.status === 'loading'}
            style={{ ...secondaryButton, opacity: blockers.status === 'loading' ? 0.6 : 1 }}
          >
            {blockers.status === 'loading' ? 'Checking…' : 'Check now'}
          </button>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
          Reads the pre-publish checklist without editing the submission. This is not a complete
          compilation or regulatory readiness check; the existing validation and publishing gates still apply.
        </p>
        {blockers.status === 'error' && (
          <div
            role="alert"
            data-testid="assistant-blockers-error"
            style={{ ...noticeStyle, background: '#fef2f2', color: '#991b1b' }}
          >
            {blockers.message}
          </div>
        )}
        {blockers.status === 'loaded' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {blockers.items.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                The checklist returned no items.
              </p>
            ) : (
              blockers.items.map((item) => {
                const meta = STATE_META[item.state];
                return (
                  <div
                    key={item.id}
                    data-testid={`assistant-blocker-${item.id}`}
                    data-state={item.state}
                    style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, fontSize: 12 }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 999,
                        background: meta.bg,
                        color: meta.color,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {meta.label}
                    </span>
                    <span style={{ color: 'var(--text)', flex: '1 1 160px', overflowWrap: 'anywhere' }}>
                      {item.label}
                      {item.required ? '' : ' (optional)'}
                      {item.failureMessage ? ` — ${item.failureMessage}` : ''}
                      {item.detail && item.detail !== item.failureMessage ? ` — ${item.detail}` : ''}
                    </span>
                  </div>
                );
              })
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
              <button
                type="button"
                data-testid="assistant-blockers-open-validate"
                onClick={() => navigate(`/submissions/${submissionId}/validate`)}
                style={{ ...secondaryButton, fontSize: 11 }}
              >
                Open validation report
              </button>
              <button
                type="button"
                data-testid="assistant-blockers-open-compiler"
                onClick={() => navigate(`/submissions/${submissionId}/compiler`)}
                style={{ ...secondaryButton, fontSize: 11 }}
              >
                Open compiler
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ---- Prepare draft corrections ---- */}
      <section style={sectionStyle} data-testid="assistant-corrections">
        <strong style={{ fontSize: 13 }}>Prepare draft corrections</strong>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
          Draft a change to Applicant, Indication or Sequence description. Nothing is applied until
          you review and confirm. Values are never invented — a blank field is yours to fill.
        </p>

        {savedMessage && (
          <div
            role="status"
            data-testid="assistant-corrections-saved"
            style={{ ...noticeStyle, background: '#dcfce7', color: '#166534' }}
          >
            {savedMessage}
          </div>
        )}

        {perms.loading ? (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Checking your permissions…</p>
        ) : perms.failed ? (
          <div
            data-testid="assistant-corrections-perms-unknown"
            style={{ ...noticeStyle, background: '#fef3c7', color: '#92400e' }}
          >
            Your permissions could not be confirmed. Reload to try again before drafting a change.
          </div>
        ) : !canWrite ? (
          <div
            data-testid="assistant-corrections-no-permission"
            style={{ ...noticeStyle, background: '#fef3c7', color: '#92400e' }}
          >
            You do not have permission to edit this submission.
          </div>
        ) : prep.stage === 'idle' ? (
          <button
            type="button"
            data-testid="assistant-corrections-prepare"
            onClick={() => void prepareDraft()}
            style={{ ...primaryButton, alignSelf: 'flex-start' }}
          >
            Prepare draft corrections
          </button>
        ) : prep.stage === 'loading' ? (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Loading current values…</p>
        ) : prep.stage === 'error' ? (
          <>
            <div
              role="alert"
              data-testid="assistant-corrections-load-error"
              style={{ ...noticeStyle, background: '#fef2f2', color: '#991b1b' }}
            >
              {prep.message}
            </div>
            <button
              type="button"
              data-testid="assistant-corrections-prepare"
              onClick={() => void prepareDraft()}
              style={{ ...secondaryButton, alignSelf: 'flex-start' }}
            >
              Try again
            </button>
          </>
        ) : locked ? (
          <>
            <div
              data-testid="assistant-corrections-locked"
              style={{ ...noticeStyle, background: '#fef3c7', color: '#92400e' }}
            >
              This submission is locked. Unlock the sequence before drafting a correction.
            </div>
            <button
              type="button"
              data-testid="assistant-corrections-cancel"
              onClick={cancelDraft}
              style={{ ...secondaryButton, alignSelf: 'flex-start' }}
            >
              Close
            </button>
          </>
        ) : prep.stage === 'editing' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {CORRECTION_FIELDS.map((f) => (
              <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
                <span style={{ color: 'var(--text-muted)' }}>{f.label}</span>
                <input
                  data-testid={`assistant-correction-input-${f.key}`}
                  maxLength={f.key === 'Applicant' ? 512 : f.key === 'SequenceDescription' ? 500 : 4000}
                  value={draft[f.key]}
                  onChange={(e) => setDraft((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  style={{
                    fontSize: 13,
                    padding: '6px 8px',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: 'var(--surface)',
                    color: 'var(--text)',
                  }}
                />
              </label>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                data-testid="assistant-corrections-review"
                onClick={() => setPrep({ stage: 'reviewing', snapshot: prep.snapshot })}
                disabled={changes.length === 0}
                style={{ ...primaryButton, opacity: changes.length === 0 ? 0.6 : 1 }}
              >
                Review changes
              </button>
              <button
                type="button"
                data-testid="assistant-corrections-cancel"
                onClick={cancelDraft}
                style={secondaryButton}
              >
                Cancel
              </button>
            </div>
            {changes.length === 0 && (
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
                Edit a field to enable review.
              </p>
            )}
          </div>
        ) : (
          // reviewing
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ fontSize: 12, margin: 0, fontWeight: 600 }}>Review before applying</p>
            {changes.map((f) => (
              <div
                key={f.key}
                data-testid={`assistant-correction-diff-${f.key}`}
                style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2, overflowWrap: 'anywhere' }}
              >
                <span style={{ color: 'var(--text-muted)' }}>{f.label}</span>
                <span style={{ color: '#991b1b', textDecoration: 'line-through' }}>
                  {currentValue(prep.snapshot, f.key) || '(empty)'}
                </span>
                <span style={{ color: '#166534' }}>{draft[f.key] || '(empty)'}</span>
              </div>
            ))}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
              <span style={{ color: 'var(--text-muted)' }}>Reason for this correction (required)</span>
              <textarea
                data-testid="assistant-corrections-reason"
                maxLength={1000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                style={{
                  fontSize: 13,
                  padding: '6px 8px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  resize: 'vertical',
                }}
              />
            </label>
            {saveError && (
              <div
                role="alert"
                data-testid="assistant-corrections-save-error"
                style={{ ...noticeStyle, background: '#fef2f2', color: '#991b1b' }}
              >
                {saveError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                data-testid="assistant-corrections-confirm"
                onClick={() => void confirmApply()}
                disabled={saving || reason.trim().length === 0 || changes.length === 0}
                style={{
                  ...primaryButton,
                  opacity: saving || reason.trim().length === 0 || changes.length === 0 ? 0.6 : 1,
                }}
              >
                {saving ? 'Applying…' : 'Confirm & apply'}
              </button>
              <button
                type="button"
                data-testid="assistant-corrections-back"
                onClick={() => setPrep({ stage: 'editing', snapshot: prep.snapshot })}
                disabled={saving}
                style={secondaryButton}
              >
                Back
              </button>
              <button
                type="button"
                data-testid="assistant-corrections-cancel"
                onClick={cancelDraft}
                disabled={saving}
                style={secondaryButton}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
